import { Button } from '@/components/ui/button';
import { Loader2, Sparkles } from '@/components/ui/icons';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import type { PlanLevel } from '@/contexts/AuthContext';
import type { BillingProduct } from '@/hooks/useBillingProducts';

interface BillingSectionProps {
  tierAccent: string;
  level: PlanLevel;
  tierLabel: string;
  periodEnd: string | null;
  handleManageSubscription: () => void;
  isManageLoading: boolean;
  subscriptionTokens: number;
  freeTokens: number;
  purchasedTokens: number;
  totalTokens: number;
  tokenPacks: BillingProduct[];
  isPurchaseLoading: boolean;
  purchaseVariables: { priceId: string } | undefined;
  purchaseTokenPack: (variables: { priceId: string }) => void;
}

export function BillingSection({
  tierAccent,
  level,
  tierLabel,
  periodEnd,
  handleManageSubscription,
  isManageLoading,
  subscriptionTokens,
  freeTokens,
  purchasedTokens,
  totalTokens,
  tokenPacks,
  isPurchaseLoading,
  purchaseVariables,
  purchaseTokenPack,
}: BillingSectionProps) {
  return (
    <section className="rounded-xl border border-telekinesis-neutral-800 bg-telekinesis-background-2 p-6">
      <h2 className="mb-5 text-sm font-medium text-telekinesis-neutral-50">
        Billing
      </h2>

      <div className="flex flex-col gap-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium',
                tierAccent,
              )}
            >
              {level === 'pro' && <Sparkles className="h-3 w-3" />}
              {tierLabel}
            </span>
            {periodEnd && (
              <span className="text-xs text-telekinesis-neutral-300">
                Renews {periodEnd}
              </span>
            )}
          </div>

          <div className="flex flex-shrink-0 items-center gap-2">
            <Button
              onClick={() => handleManageSubscription()}
              className="rounded-full font-light"
              variant="dark"
              disabled={isManageLoading}
            >
              {isManageLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : level !== 'free' ? (
                'Manage'
              ) : (
                'Manage billing'
              )}
            </Button>
            {level === 'free' && (
              <Link to="/subscription">
                <Button
                  className="rounded-full font-light"
                  variant="light"
                >
                  Upgrade
                </Button>
              </Link>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          {level !== 'free' && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-telekinesis-neutral-200">
                Subscription tokens
              </span>
              <span className="text-xs tabular-nums text-telekinesis-neutral-50">
                {subscriptionTokens.toLocaleString()}
              </span>
            </div>
          )}
          {freeTokens > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-telekinesis-neutral-200">
                Daily free tokens
              </span>
              <span className="text-xs tabular-nums text-telekinesis-neutral-50">
                {freeTokens.toLocaleString()}
              </span>
            </div>
          )}
          {purchasedTokens > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-telekinesis-neutral-200">
                Purchased tokens
              </span>
              <span className="text-xs tabular-nums text-telekinesis-neutral-50">
                {purchasedTokens.toLocaleString()}
              </span>
            </div>
          )}
          <div className="mt-1 flex items-center justify-between border-t border-telekinesis-neutral-800 pt-3">
            <span className="text-sm text-telekinesis-neutral-50">
              Total available
            </span>
            <span className="text-sm font-medium tabular-nums text-telekinesis-neutral-50">
              {totalTokens.toLocaleString()}
            </span>
          </div>
        </div>

        {tokenPacks.length > 0 && (
          <div className="flex flex-col gap-2 border-t border-telekinesis-neutral-800 pt-5">
            <div className="flex items-baseline justify-between">
              <div className="text-sm text-telekinesis-neutral-50">
                Buy more tokens
              </div>
              <div className="text-xs text-telekinesis-neutral-200">
                Never expire
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {tokenPacks.map((pack) => {
                const isThisPending =
                  isPurchaseLoading &&
                  purchaseVariables?.priceId === pack.stripePriceId;
                return (
                  <button
                    key={pack.id}
                    type="button"
                    disabled={isPurchaseLoading}
                    onClick={() =>
                      purchaseTokenPack({ priceId: pack.stripePriceId })
                    }
                    className={cn(
                      'relative flex flex-col items-start rounded-lg border border-telekinesis-neutral-800 bg-telekinesis-background-1 px-3 py-2.5 text-left transition-colors',
                      'hover:border-telekinesis-gold/40 hover:bg-telekinesis-neutral-800/40',
                      'disabled:opacity-50',
                    )}
                  >
                    {isThisPending && (
                      <Loader2 className="absolute right-2 top-2 h-3.5 w-3.5 animate-spin text-telekinesis-neutral-200" />
                    )}
                    <div className="text-sm font-medium tabular-nums text-telekinesis-neutral-50">
                      {pack.tokenAmount.toLocaleString()}
                    </div>
                    <div className="mt-0.5 text-xs tabular-nums text-telekinesis-neutral-200">
                      ${(pack.priceCents / 100).toFixed(2)}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
